import React, { FC, ReactNode, useState, useMemo, useEffect } from "react";

import { withThemeProvider, useTheme } from "@narvar/nth-hook-theme";
import Thumbnail from "@narvar/nth-bit-thumbnail";
import IconX from "@narvar/nth-bit-icon-x";

import LanguageKeys from "../../consts/language_en_US";
import Language from "../Language";
import FileUploadButton, { FileUploadButtonProps } from "../FileUploadButton";
import IconFilledButton from "../IconFilledButton";
import { useStyles } from "./FileUploadStyles";

export type FileUploadProps = FileUploadButtonProps & {
  label?: ReactNode | string;
  caption?: ReactNode | string;
  maxCount?: number;
  value?: File[];
  onChange?: (acceptedFiles: File[]) => void;
};

const tempLanguageKeys = {
  returnsReasonsPicturesUploadErrorMaxSize:
    LanguageKeys.returnsReasonsPicturesUploadErrorMaxSize,
  returnsReasonsPicturesUploadErrorMaxCount:
    LanguageKeys.returnsReasonsPicturesUploadErrorMaxCount,
};

const FileUpload: FC<FileUploadProps> = ({
  label,
  caption,
  maxCount,
  value,
  onChange,
  ...props
}) => {
  const {
    captiontextDefaultNone: captionStyle,
    captiontextErrorNone: captionStyleError,
  } = useTheme();

  const styles = useStyles();

  const {
    returnsReasonsPicturesUploadErrorMaxSize,
    returnsReasonsPicturesUploadErrorMaxCount,
  } = tempLanguageKeys;

  const [errorCode, setErrorCode] = useState<"maxSize" | "maxCount" | "">();
  const [files, setFiles] = useState<File[]>([]);
  const previews = useMemo<
    { name: string; type: string; url: string; file: File }[]
  >(
    () =>
      files.map((file) => ({
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        file,
      })),
    [files],
  );

  useEffect(() => {
    if (maxCount && maxCount > 0 && files.length > maxCount) {
      setErrorCode("maxCount");
    }
  }, [maxCount, files]);

  useEffect(() => {
    setFiles(value ?? []);
  }, [value]);

  const handleChange = (acceptFiles: File[], rejectedFiles: File[]) => {
    const updates = [...files, ...acceptFiles];
    setFiles(updates);

    if (rejectedFiles?.length > 0) {
      setErrorCode("maxSize");
    } else {
      setErrorCode("");
    }
    onChange?.(updates);
  };

  const removeFile = (file: File) => {
    const updates = files.filter((f) => f !== file);
    setFiles(updates);
    setErrorCode("");
    onChange?.(updates);
  };

  return (
    <>
      <FileUploadButton {...props} onChange={handleChange}>
        {label}
      </FileUploadButton>
      {caption && <div style={captionStyle}>{caption}</div>}
      <div className={styles.previews}>
        {previews.map((p, i) => (
          <div className={styles.preview} key={i}>
            <Thumbnail
              src={p.url}
              alt={p.name}
              s={{ width: 150, display: "block" }}
            />
            <IconFilledButton
              Icon={IconX}
              s={{ position: "absolute", top: 4, right: 4 }}
              sIcon={{ width: 12, height: 12 }}
              onClick={() => removeFile(p.file)}
            />
          </div>
        ))}
      </div>
      {errorCode === "maxSize" && (
        <div style={captionStyleError}>
          <Language
            langKey="returnsReasonsPicturesUploadErrorMaxSize"
            defaultString={returnsReasonsPicturesUploadErrorMaxSize}
          />
        </div>
      )}
      {errorCode === "maxCount" && (
        <div style={captionStyleError}>
          <Language
            langKey="returnsReasonsPicturesUploadErrorMaxCount"
            defaultString={returnsReasonsPicturesUploadErrorMaxCount}
          />
        </div>
      )}
    </>
  );
};

export default withThemeProvider(FileUpload, {
  themeTokens: ["captiontextDefaultNone", "captiontextErrorNone"],
});
